'use client'
import Link from 'next/link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ShieldCheck, ChevronRight } from 'lucide-react'
import { Sparkline } from './sparkline'
import { pillarTheme } from '@/lib/phishtrainer/pillar-theme'
import type { PhishPillarCode } from '@/lib/phishtrainer/types'
import { useTranslation } from '@/lib/i18n'

export interface PillarItem {
  code: string              // 'PF1'..'PF5'
  letter: string            // 'P', 'H', 'I', 'S', 'H'
  label: string             // 'Pressure', etc.
  mastery: number           // 0..100
  spark: number[]           // recent history
  color: string             // pillar hex (superseded by pillar-theme; kept for compat)
}

export interface PillarMasteryStripProps {
  pillars: PillarItem[]
  /**
   * Where a pillar tile navigates. Defaults to the LEARNER drill-down so the
   * play home is unchanged; the admin user page overrides it to stay inside
   * /phishtrainer instead of being thrown into the learner app.
   */
  pillarHref?: (code: string) => string
}

/**
 * Pillar-mastery strip, shared by the learner's play home and the ADMIN user
 * page. Each tile links into that pillar's attribution drill-down.
 *
 * `pillarHref` exists because the destination is realm-specific and the target
 * used to be hardcoded to `/play/me/pillars/[code]`. The admin user page renders
 * this same component, so clicking a pillar there navigated the admin OUT of the
 * admin area and into the learner app — a different realm, a different session,
 * and someone else's data. The admin now passes a route that keeps them in
 * /phishtrainer, and the learner keeps the default.
 */
export function PillarMasteryStrip({
  pillars,
  pillarHref = (code) => `/play/me/pillars/${code}`,
}: PillarMasteryStripProps) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-2 pb-3">
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" /> {t('learner.cards.pillarMastery.title')}
        </CardTitle>
        <span className="text-xs text-muted-foreground">{t('learner.cards.pillarMastery.hint')}</span>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {pillars.map((p) => {
            const theme = pillarTheme(p.code as PhishPillarCode)
            const trend = (p.spark[p.spark.length - 1] ?? 0) - (p.spark[0] ?? 0)
            const trendColor =
              trend > 0 ? 'text-emerald-500 dark:text-emerald-400' : trend < 0 ? 'text-red-500 dark:text-red-400' : 'text-muted-foreground'
            return (
              <Link
                key={p.code}
                href={pillarHref(p.code)}
                aria-label={t('learner.cards.pillarMastery.tileLabel', { label: p.label, mastery: p.mastery })}
                className="group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <div className={`flex h-full flex-col rounded-xl border border-t-[3px] border-border ${theme.border} bg-muted/30 p-3 transition-all duration-200 group-hover:-translate-y-1 group-hover:shadow-lg`}>
                  <div className="flex items-start justify-between">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg font-bold text-white ${theme.fill}`}>
                      {p.letter}
                    </div>
                    <span className={`rounded-md border border-border px-1.5 py-0.5 font-mono text-[10px] font-semibold ${theme.text}`}>{p.code}</span>
                  </div>
                  <div className="mt-2 truncate text-[11px] text-muted-foreground">{p.label}</div>
                  <div className="mt-0.5 flex items-baseline gap-1.5">
                    <span className="text-2xl font-bold tabular-nums text-foreground">{p.mastery}</span>
                    <span className="text-[10px] text-muted-foreground">/ 100</span>
                    {/* 7-day change — only when it actually moved (a bare "0" read
                        as a mystery value). Arrow makes the direction explicit. */}
                    {trend !== 0 && (
                      <span className={`ms-auto text-[10px] font-semibold tabular-nums ${trendColor}`} title={t('learner.cards.pillarMastery.trendTitle')}>
                        {trend > 0 ? '▲' : '▼'} {Math.abs(trend)}
                      </span>
                    )}
                  </div>
                  <div className="-mx-1 mt-1">
                    <Sparkline data={p.spark} color={theme.hex} height={30} />
                  </div>
                  <div className={`mt-1 flex items-center justify-end gap-0.5 text-[11px] font-medium opacity-0 transition-opacity group-hover:opacity-100 ${theme.text}`}>
                    {t('learner.cards.common.seeTheRounds')}
                    <ChevronRight className="h-3.5 w-3.5 rtl-flip transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5" />
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
